// Hierarchischer Kategorie-Tree-Picker fuer den Gambio-Artikel-Slot - v2 (fix/gambio-picker-v2-maincat-mpn).
//
// v1 (Etappe 2 des Slot-Tree-Picker-Umbaus) hatte drei Probleme:
//   1. Hauptkategorie-Wahl war ein winziger Stern, der nur bei angehakten Zeilen sichtbar war
//      (nicht auffindbar/nutzbar).
//   2. Voller innerHTML-Neuaufbau des GESAMTEN Baums bei jeder Aenderung/jedem Tastendruck - bei
//      ~1000 Kategorien zu schwer.
//   3. (siehe listing-detail-pane.ts - dort stand zusaetzlich veralteter/widerspruechlicher Text.)
//
// v2-Design (siehe Task-Briefing):
//   - "Gewaehlte Kategorien" oben: kompakte Liste NUR der ausgewaehlten Kategorien, je Zeile ein
//     Radio (Hauptkategorie-Wahl - klar & immer sichtbar), Name + Breadcrumb, X-Button zum Entfernen.
//   - Suchfeld (debounced ~200ms): bei Suchbegriff eine FLACHE Trefferliste (Name enthaelt Begriff,
//     case-insensitive) mit Breadcrumb, gedeckelt auf 50 Treffer + Hinweis auf den Rest. Kein Baum
//     waehrend der Suche.
//   - Baum (nur wenn Suchfeld leer): standardmaessig eingeklappt, nur Roots gerendert; Chevron
//     expandiert einen Knoten → Kinder werden LAZY gerendert (nur aufgeklappte Zweige, nie der
//     gesamte Baum).
//
// Re-Render-Strategie: das Such-Input-Element wird NIE per innerHTML neu aufgebaut (nur per
// addEventListener beobachtet) - dadurch bleiben Fokus/Caret automatisch erhalten, ohne Restore-Hack.
// Nur der "Gewaehlte Kategorien"-Bereich und der Baum/Such-Trefferbereich werden gezielt neu gerendert
// (scoped re-render), NIE der komplette Host. Bei 1000 Knoten liegen im Ruhezustand nur Roots +
// aufgeklappte Zweige im DOM.

export interface CategoryTreeNode {
  gambioId: number;
  parentId: number;
  name: string;
}

export interface CategoryPickerOptions {
  tree: CategoryTreeNode[];
  selected: number[];
  mainId: number | null;
  onChange: (state: { selected: number[]; mainId: number | null }) => void;
}

const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_RESULT_CAP = 50;

/** Baut byId-Index + parentId→children-Gruppierung (sortiert). `parentId===0` ODER ein
 * unbekannter/fehlender Vater gilt als Root (Konvention wie v1). */
function buildIndex(flat: CategoryTreeNode[]): {
  byId: Map<number, CategoryTreeNode>;
  childrenOf: Map<number, CategoryTreeNode[]>;
} {
  const byId = new Map<number, CategoryTreeNode>(flat.map((n) => [n.gambioId, n]));
  const childrenOf = new Map<number, CategoryTreeNode[]>();
  for (const n of flat) {
    const parentKey = n.parentId && byId.has(n.parentId) ? n.parentId : 0;
    if (!childrenOf.has(parentKey)) childrenOf.set(parentKey, []);
    childrenOf.get(parentKey)!.push(n);
  }
  for (const list of childrenOf.values()) list.sort((a, b) => a.name.localeCompare(b.name, 'de'));
  return { byId, childrenOf };
}

export function renderCategoryTreePicker(host: HTMLElement, opts: CategoryPickerOptions): void {
  const { byId, childrenOf } = buildIndex(opts.tree);
  const roots = childrenOf.get(0) ?? [];
  const selected = new Set<number>(opts.selected);
  let mainId: number | null = opts.mainId;
  const expanded = new Set<number>(); // leer = komplett eingeklappt (Default)
  let searchTerm = '';
  let debounceHandle: number | undefined;

  /** Vorfahren-Namen (ohne den Knoten selbst), aeltester zuerst - fuer Breadcrumbs. */
  function breadcrumb(gambioId: number): string {
    const parts: string[] = [];
    let cur = byId.get(gambioId);
    while (cur && cur.parentId) {
      const parent = byId.get(cur.parentId);
      if (!parent) break;
      parts.unshift(parent.name);
      cur = parent;
    }
    return parts.join(' › ');
  }

  function emitChange(): void {
    opts.onChange({ selected: Array.from(selected), mainId });
  }

  function toggleSelect(id: number): void {
    if (selected.has(id)) {
      selected.delete(id);
      if (mainId === id) {
        const rest = Array.from(selected);
        mainId = rest[0] ?? null;
      }
    } else {
      selected.add(id);
      if (mainId === null) mainId = id;
    }
    emitChange();
    renderSelected();
    renderBrowse();
  }

  function setMain(id: number): void {
    if (!selected.has(id)) return; // Hauptkategorie muss ausgewaehlt sein
    if (mainId === id) return;
    mainId = id;
    emitChange();
    renderSelected();
  }

  function toggleExpand(id: number): void {
    if (expanded.has(id)) expanded.delete(id); else expanded.add(id);
    renderBrowse();
  }

  // ── Markup: Host EINMAL aufbauen, Such-Input NIE per innerHTML neu erzeugen ──────────────
  host.innerHTML = `
    <div class="go-catpicker">
      <div id="go-catpicker-selected"></div>
      <hr style="border:none;border-top:1px solid #E6E6E9;margin:10px 0;">
      <div class="v-input-group" style="margin-bottom:8px;">
        <input type="text" class="v-input" id="go-catpicker-search" placeholder="Kategorie suchen…" style="width:100%;box-sizing:border-box;">
      </div>
      <div id="go-catpicker-browse"></div>
    </div>`;

  const selectedHost = host.querySelector<HTMLElement>('#go-catpicker-selected')!;
  const browseHost = host.querySelector<HTMLElement>('#go-catpicker-browse')!;
  const searchInput = host.querySelector<HTMLInputElement>('#go-catpicker-search')!;

  searchInput.addEventListener('input', () => {
    const val = searchInput.value;
    if (debounceHandle !== undefined) window.clearTimeout(debounceHandle);
    debounceHandle = window.setTimeout(() => {
      searchTerm = val;
      renderBrowse();
    }, SEARCH_DEBOUNCE_MS);
  });

  // ── "Gewaehlte Kategorien" - kompakte Liste; Stern-Button + Badge = Hauptkategorie ──────────
  // Die Hauptkategorie ist unmissverstaendlich markiert: gefuellter Stern + gruenes
  // „Hauptkategorie"-Badge + hervorgehobene Zeile. Ein Klick auf den (leeren) Stern einer anderen
  // Zeile macht diese zur Hauptkategorie. (Vorher ein nacktes Radio → unklar, was es bedeutet.)
  const HEADER = `<div class="v-text-muted" style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px;">Gewählte Kategorien</div>`;
  function renderSelected(): void {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      selectedHost.innerHTML = `${HEADER}<div class="v-text-muted" style="padding:4px 2px;">Noch keine Kategorien gewählt.</div>`;
      return;
    }
    const items = ids
      .map((id) => ({ id, name: byId.get(id)?.name ?? `Kategorie ${id}`, path: breadcrumb(id) }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
    const hint = `<div class="v-text-muted" style="font-size:11px;margin:-2px 0 6px;">Der <span class="v-icon fa-solid fa-star" style="color:#E0A100;font-size:10px;"></span>-Stern markiert die <strong>Hauptkategorie</strong> - auf einen anderen Stern klicken, um sie zu wechseln.</div>`;
    selectedHost.innerHTML = `${HEADER}${hint}
      <ul style="list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:2px;max-height:220px;overflow:auto;">
        ${items.map((it) => {
          const isMain = mainId === it.id;
          const star = `<button type="button" class="v-button v-button--ghost" data-cat-main-star="${it.id}"
              title="${isMain ? 'Hauptkategorie' : 'Als Hauptkategorie festlegen'}" aria-pressed="${isMain ? 'true' : 'false'}"
              style="padding:2px 5px;min-width:0;line-height:1;flex:none;">
              <span class="v-icon ${isMain ? 'fa-solid' : 'fa-regular'} fa-star" style="${isMain ? 'color:#E0A100;' : ''}"></span>
            </button>`;
          const badge = isMain
            ? `<span style="flex:none;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;color:#3F7A17;background:#EAF4DD;border-radius:10px;padding:1px 7px;">Hauptkategorie</span>`
            : '';
          return `
          <li style="display:flex;align-items:center;gap:8px;padding:4px 6px;border-radius:6px;${isMain ? 'background:#F2F7EA;' : ''}">
            ${star}
            <div style="flex:1;min-width:0;">
              <div style="display:flex;align-items:center;gap:6px;min-width:0;">
                <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;${isMain ? 'font-weight:600;' : ''}">${escapeHtml(it.name)}</span>
                ${badge}
              </div>
              ${it.path ? `<div class="v-text-muted" style="font-size:10.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(it.path)}</div>` : ''}
            </div>
            <button type="button" class="v-button v-button--ghost" data-cat-remove="${it.id}" title="Entfernen"
                    style="padding:2px 6px;min-width:0;line-height:1;flex:none;">
              <span class="v-icon fa-light fa-xmark"></span>
            </button>
          </li>`;
        }).join('')}
      </ul>`;
    selectedHost.querySelectorAll<HTMLButtonElement>('[data-cat-main-star]').forEach((btn) => {
      btn.addEventListener('click', () => setMain(Number(btn.dataset.catMainStar)));
    });
    selectedHost.querySelectorAll<HTMLButtonElement>('[data-cat-remove]').forEach((btn) => {
      btn.addEventListener('click', () => toggleSelect(Number(btn.dataset.catRemove)));
    });
  }

  // ── Such-Trefferliste (flach, gedeckelt) ──────────────────────────────────────────────────
  function renderSearchResults(term: string): void {
    const needle = term.trim().toLowerCase();
    const matches = opts.tree
      .filter((n) => n.name.toLowerCase().includes(needle))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
    if (matches.length === 0) {
      browseHost.innerHTML = `<div class="v-text-muted" style="padding:6px 2px;">Keine Kategorie gefunden.</div>`;
      return;
    }
    const capped = matches.slice(0, SEARCH_RESULT_CAP);
    const remaining = matches.length - capped.length;
    browseHost.innerHTML = `
      <ul style="list-style:none;margin:0;padding:0;max-height:320px;overflow:auto;">
        ${capped.map((n) => searchRowHtml(n)).join('')}
      </ul>
      ${remaining > 0 ? `<div class="v-text-muted" style="padding:4px 2px;font-size:11px;">… ${remaining} weitere - Suche verfeinern.</div>` : ''}`;
    wireCheckboxes(browseHost);
  }

  function searchRowHtml(n: CategoryTreeNode): string {
    const isChecked = selected.has(n.gambioId);
    const path = breadcrumb(n.gambioId);
    return `
      <li style="margin:1px 0;">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;padding:3px 4px;border-radius:6px;">
          <input type="checkbox" data-cat-check="${n.gambioId}" ${isChecked ? 'checked' : ''} style="cursor:pointer;flex:none;">
          <div style="flex:1;min-width:0;">
            <div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(n.name)}</div>
            ${path ? `<div class="v-text-muted" style="font-size:10.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(path)}</div>` : ''}
          </div>
        </label>
      </li>`;
  }

  // ── Baum (lazy: nur Roots + aufgeklappte Zweige) ──────────────────────────────────────────
  function renderTree(): void {
    const rootsHtml = roots.map((n) => treeNodeHtml(n)).join('');
    browseHost.innerHTML = `<ul style="list-style:none;margin:0;padding:0;max-height:320px;overflow:auto;">${rootsHtml}</ul>`;
    wireCheckboxes(browseHost);
    browseHost.querySelectorAll<HTMLButtonElement>('[data-cat-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => toggleExpand(Number(btn.dataset.catToggle)));
    });
  }

  function treeNodeHtml(node: CategoryTreeNode): string {
    const kids = childrenOf.get(node.gambioId) ?? [];
    const hasChildren = kids.length > 0;
    const isExpanded = expanded.has(node.gambioId);
    const isChecked = selected.has(node.gambioId);
    const chevron = hasChildren
      ? `<button type="button" class="v-button v-button--ghost" data-cat-toggle="${node.gambioId}"
           style="padding:2px 4px;min-width:0;line-height:1;" title="${isExpanded ? 'Einklappen' : 'Ausklappen'}">
           <span class="v-icon fa-light ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}"></span>
         </button>`
      : `<span style="display:inline-block;width:22px;"></span>`;
    // Lazy: Kinder-<ul> nur rendern, wenn dieser Knoten aufgeklappt ist - nie der gesamte Baum.
    const childrenHtml = hasChildren && isExpanded
      ? `<ul style="list-style:none;margin:0;padding-left:20px;">${kids.map((c) => treeNodeHtml(c)).join('')}</ul>`
      : '';
    return `
      <li data-cat-node="${node.gambioId}" style="margin:1px 0;">
        <div style="display:flex;align-items:center;gap:2px;">
          ${chevron}
          <label style="display:flex;align-items:center;gap:6px;cursor:pointer;flex:1;min-width:0;padding:2px 4px;border-radius:6px;">
            <input type="checkbox" data-cat-check="${node.gambioId}" ${isChecked ? 'checked' : ''} style="cursor:pointer;">
            <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(node.name)}</span>
          </label>
        </div>
        ${childrenHtml}
      </li>`;
  }

  function wireCheckboxes(scope: HTMLElement): void {
    scope.querySelectorAll<HTMLInputElement>('[data-cat-check]').forEach((input) => {
      input.addEventListener('change', () => toggleSelect(Number(input.dataset.catCheck)));
    });
  }

  function renderBrowse(): void {
    if (searchTerm.trim()) renderSearchResults(searchTerm);
    else renderTree();
  }

  renderSelected();
  renderBrowse();
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
